import { VideoStartEvent } from '../../types'
import ReactDOM from 'react-dom/client'
import { BaseVideoPlayer } from './BaseVideoPlayer'
import { createRef, RefObject } from 'react'
import { flushSync } from 'react-dom'
import { isErtDebugModeEnabled, getVideoIdentifier } from '../../rrwebUtils'
import VideoAccessEndedIcon from '../../../../../../assets/images/video-access-ended.svg'
import NotAvailableIcon from '../../../../../../assets/images/not-available-icon.svg'
import { disallowedReasonMessages } from '../../../ErtPreprocessor'

export class PlayerError extends BaseVideoPlayer {
  constructor(appendTo: HTMLElement, videoStartEvent: VideoStartEvent) {
    const { videoAccessError } = videoStartEvent

    if (!videoAccessError) {
      throw new Error('You must specify a video access error to use the VideoErrorPlaceholder')
    }

    const videoShellRef = createRef<HTMLDivElement>()
    const renderCallback = getRenderCallback(appendTo, videoShellRef, videoStartEvent)
    flushSync(renderCallback)

    if (!videoShellRef.current) {
      throw new Error('Video shell not properly initialized')
    }

    super(appendTo, videoShellRef.current, undefined, videoStartEvent, [])
  }

  detachListeners() {
    // no video element, nothing to detach
  }

  reAddEventListeners() {
    // because the placeholder has no video element, there are no listeners to re-attach and this will fail, so overriding to do nothing
  }

  isErrorPlaceholder() {
    return true
  }
}

const isUnauthorized = (videoStartEvent: VideoStartEvent): boolean => {
  const errorMessage = videoStartEvent.videoAccessError?.message
  if (!errorMessage) return false
  return Object.values(disallowedReasonMessages).includes(errorMessage) || errorMessage === 'Access denied.'
}

export const getRenderCallback = (appendTo: HTMLElement, videoShellRef: RefObject<HTMLDivElement | null>, videoStartEvent: VideoStartEvent) => {
  return () => {
    ReactDOM.createRoot(appendTo).render(
      <div
        style={{
          boxSizing: 'border-box',
          lineHeight: '1.5',
          height: '0',
          width: '0',
          visibility: 'hidden',
          backgroundColor: 'white',
          padding: '1rem',
          color: '#525253', // hardcoding color because we currently are not importing global styles into rrweb player iframes
          backgroundImage: videoStartEvent.videoAccessError?.videoOmitted
            ? 'repeating-linear-gradient(135deg, #f7f7f7 0, #f7f7f7 8px, #ffffff 8px, #ffffff 16px)'
            : undefined,
          display: 'flex',
          gap: 'clamp(0.25rem, 5%, 1rem)',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          border: '1px solid gray'
        }}
        ref={videoShellRef}
        title={getVideoIdentifier(videoStartEvent)}
      >
        {videoStartEvent.videoAccessError?.videoOmitted ? (
          <div style={{ color: '#cccccc', fontSize: '2rem' }}>{videoStartEvent.videoAccessError?.message}</div>
        ) : (
          <>
            {isUnauthorized(videoStartEvent) ? (
              <img
                src={VideoAccessEndedIcon}
                alt="Video Access Ended Icon"
                style={{ width: 'clamp(3rem, 25%, 4.5rem)', height: 'clamp(3rem, 25%, 4.5rem)' }}
              />
            ) : (
              <img
                src={NotAvailableIcon}
                alt="Not Available Icon"
                style={{ width: 'clamp(3rem, 25%, 4.5rem)', height: 'clamp(3rem, 25%, 4.5rem)' }}
              />
            )}
            <div style={{ gap: '0.125rem', display: 'flex', flexDirection: 'column', alignItems: 'center', fontSize: '1rem', fontWeight: 400 }}>
              <div>{videoStartEvent.videoAccessError?.message}</div>
            </div>
            {isErtDebugModeEnabled() && (
              <div style={{ fontSize: '0.6rem', color: '#555555' }}>
                <div>{videoStartEvent.cameraId}</div>
                <div>{videoStartEvent.videoPlaceholderName}</div>
                <div>{videoStartEvent.startTimeEpoch}</div>
              </div>
            )}
          </>
        )}
      </div>
    )
  }
}
